<template>
  <div v-if="model.Id" class="ticket-layout px-4 row">
    <div class="ticket-main col-xl-10 col-lg-9 col-md-8">
      <!-- Encabezado del ticket -->
      <div class="ticket-header">
        <div class="ticket-header-left">
          <h1>{{ $t("ticket.detalle.titulo") }} {{ model.CodigoSecuencial.toString().padStart(5, "0") }}</h1>
          <p class="ticket-asunto texto-light m-0">{{ model.Asunto }}</p>
        </div>
        <div class="ticket-header-right">
          <div v-if="esBackoffice">
            <div v-if="showSolicitarCierre" class="ticket-close-request">
              <DxButton @click="handleCloseRequest" class="close-request-button">
                {{ $t("ticket.cierre.solicitar") }}
              </DxButton>
            </div>
          </div>
          <div v-if="esBackoffice">
            <label class="dx-field-item-label-content dx-field-item-label dx-field-item-label-location-top dx-field-item-label-text" for="prioridad-select">{{
              $t("ticket.prioridad.label")
            }}</label>
            <DxSelectBox
              v-model="model.Prioridad"
              :placeholder="$t('ticket.prioridad.placeholder')"
              v-bind="{ ...getSelectBoxDefaultProperties(), ...selectPrioridadesOptions }"
              @selection-changed="handlePrioridadChange"
              id="prioridad-select"
            ></DxSelectBox>
          </div>
        </div>
      </div>

      <!-- Conversación del ticket -->
      <div class="ticket-messages">
        <div
          v-for="(mensaje, index) in model.Mensajes"
          class="message"
          :class="{
            'user-message': props.esBackoffice ? mensaje.EsMensajeSistema : !mensaje.EsMensajeSistema,
            'reply-message': props.esBackoffice ? !mensaje.EsMensajeSistema : mensaje.EsMensajeSistema,
          }"
          :id="index === model.Mensajes.length - 1 ? 'last-message' : `$index-message-${index}`"
        >
          <div v-if="mensaje.EsMensajeSistema && !props.esBackoffice" class="circle-icon-system">
            <i class="fas fa-user-headset"></i>
          </div>
          <div v-if="!mensaje.EsMensajeSistema && props.esBackoffice" class="circle-icon">
            <i class="fas fa-user"></i>
          </div>
          <div class="message-content">
            <div class="message-top texto-light">{{ mensaje.Emisor }}:</div>
            <p>
              {{ mensaje.Mensaje }}
            </p>
            <div class="message-attachments">
              <div v-for="adjunto in mensaje.Adjuntos" class="d-flex align-items-center documento gap-2" @click="descargaAdjunto(adjunto)">
                <i class="fa fa-paperclip"></i>
                <span> {{ getFilename(adjunto) }} </span>
              </div>
            </div>
            <div class="message-bottom">
              <span class="texto-light">{{ new Date(mensaje.Fecha).toLocaleString("es-ES") }}</span>
              <div class="message-status" v-if="props.esBackoffice ? mensaje.EsMensajeSistema : !mensaje.EsMensajeSistema">
                <i v-if="mensaje.LeidoPorRolContrario" class="fa-solid fa-check-double check-double"></i>
                <i v-else class="fa-regular fa-check check-single"></i>
              </div>
            </div>
          </div>
          <div v-if="mensaje.EsMensajeSistema && props.esBackoffice" class="circle-icon-system">
            <i class="fas fa-user-headset"></i>
          </div>
          <div v-if="!mensaje.EsMensajeSistema && !props.esBackoffice" class="circle-icon">
            <i class="fas fa-user"></i>
          </div>
        </div>
      </div>

      <!-- Área de respuesta -->
      <div
        v-if="model.PuedeEscribirMensajes"
        :class="[
          'ticket-reply',
          { 'con-adjuntos': Object.values(ficheros).filter((fichero) => fichero.file !== null && fichero.file !== undefined).length > 0 },
        ]"
      >
        <div class="reply-attach-file">
          <MultipleFileUploader
            :max-files="5"
            v-model:ficheros="ficheros"
            :textForAddAttachmentLabel="''"
            :textForAddAttachmentButton="''"
            :textForNonDownloadableFiles="''"
            mode="clip"
          >
          </MultipleFileUploader>
        </div>
        <div class="reply-input">
          <textarea v-model="textoMensaje" :placeholder="$t('ticket.detalle.mensaje.placeholder')" rows="3" />
          <button class="send-button" @click="sendMessage">➤</button>
        </div>
      </div>

      <!-- Área de cierre o rechazo de cierre (solo no-backoffice) -->
      <div v-if="!esBackoffice && model.PuedeCerrarse" class="ticket-close-actions">
        <div class="close-ticket-text">{{ $t("ticket.detalle.solicitud_cierre") }}</div>
        <div class="close-ticket-buttons">
          <DxButton @click="handleCloseTicket" class="close-ticket-button" type="default">
            {{ $t("ticket.detalle.aceptar-cierre") }}
          </DxButton>
          <DxButton @click="handleRejectCloseTicket" class="reject-close-button">
            {{ $t("ticket.detalle.rechazar-cierre") }}
          </DxButton>
        </div>
      </div>
    </div>

    <!-- Panel lateral derecho -->
    <div class="ticket-sidebar col-xl-2 col-lg-3 col-md-4 mt-sm-3 mt-0">
      <div class="sidebar-box">
        <h6>{{ $t("ticket.detalle.detalle_ticket") }}</h6>
        <label class="dx-field-item-label-text">{{ $t("ticket.detalle.estado") }}</label>
        <div class="status-type-container">
          <div  :class="['d-inline-block rounded py-1 px-2', `estado ${model.Estado?.replace(/\s+/g, '_')}`]">
            {{ $t(`ticket.estado.${model.Estado}`) }}
          </div>
        </div>

        <label class="dx-field-item-label-text mt-3">{{ $t("ticket.detalle.tipologia") }}</label>
        <div>
          <span class="ticket-type">
            {{ $t(`ticket.tipo.${model.Tipo}`) }}
            <template v-if="model.Subtipo"> / {{ $t(`ticket.subtipo.${model.Subtipo}`) }}</template>
          </span>
        </div>

        <label class="dx-field-item-label-text mt-3">{{ $t("ticket.detalle.responsable") }}</label>
        <div v-if="model.Responsable && model.Responsable.trim() !== ''">{{ model.Responsable }}</div>
        <div v-else>{{ $t("ticket.detalle.no_responsable") }}</div>
        <DxButton
          v-bind="secondaryButtonProps()"
          :visible="showAutoAsignarResponsable"
          :text="$t('chatOperacion.responsable.autoasignar_responsable')"
          :class="'w-100 mt-3'"
          @click="autoAsignarResponsable"
        />
        <DxButton
          v-bind="secondaryButtonProps()"
          :visible="showAsignarResponsable"
          :text="$t('chatOperacion.responsable.asignar_responsable')"
          :class="'w-100 mt-3'"
          @click="asignarResponsable"
        />
      </div>

      <TicketSidebarDetalleEntidad :model="model"
        @ver-detalle-operacion="emit('ver-detalle-operacion', model)"
        @ver-detalle-borrador="emit('ver-detalle-borrador', model)" />

      <!-- Caja para listar adjuntos -->
      <div class="sidebar-box">
        <h6>{{ $t("ticket.detalle.adjuntos.titulo") }}</h6>
        <AdjuntosList :adjuntos="todosLosAdjuntos" :texto-no-adjuntos="$t('ticket.detalle.no_hay_adjuntos')" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { CreateMensajeTicketModel, DetailTicketModel } from "@api/api";
import { useApi } from "@api/integration";
import { computed, nextTick, onBeforeMount, onMounted, onUnmounted, reactive, ref, watch } from "vue";
import { useAuthorization } from "@/.shared/services/auth/authorization";
import { PermissionsApp } from "@/.shared/services/auth/authorization/authorization-service-impl";
import { DxSelectBox, DxButton } from "devextreme-vue";
import { getSelectBoxDefaultProperties } from "@helpers/grid-helper";
import MultipleFileUploader from "@/.shared/components/file-uploading/MultipleFileUploader.vue";
import { toastError, toastSuccess } from "@/.shared/services/notification-service";
import { useIntl } from "@services/i18n";
import FileSaver from "file-saver";
import { useModalService } from "@/.shared/services/modal-service";
import { messageBoxResult } from "../dialogs/message-box";
import { secondaryButtonProps } from '@/.shared/helpers/button-helper';
import { useAuthentication } from "@/.shared/services/auth";
import AdjuntosList from "@/.shared/components/AdjuntosList.vue";
import InputSelectBox, { type InputSelectBoxModalResult } from "@/.shared/components/dialogs/InputSelectBox.vue";
import TicketSidebarDetalleEntidad from "./ticket-detail-sidebar/TicketSidebarDetalleEntidad.vue";
import { getDocumentalFileName as getFilename } from "@/.shared/helpers/documental-file-name-helper";

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  esBackoffice: {
    type: Boolean,
    required: true,
  },
});

const model = reactive<DetailTicketModel>({} as DetailTicketModel);
const api = useApi();
const auth = useAuthentication();
const autho = useAuthorization();
const isInitialLoad = reactive({ value: true });
const ficheros = ref<Ficheros>({});
const textoMensaje = ref<string>("");
const intl = useIntl();
const modal = useModalService();

const emit = defineEmits<{
  "ver-detalle-operacion": [model: DetailTicketModel];
  "ver-detalle-borrador": [model: DetailTicketModel];
}>()

const selectPrioridadesOptions = reactive<any>({
  valueExpr: "Denominacion",
  displayExpr: "translatedText",
  showClearButton: false,
});

onBeforeMount(async () => {
  if (esBackoffice) {
    let prioridades = await api.callStandardApi("getPrioridadesTicketSelector", { showLoading: false });
    selectPrioridadesOptions.dataSource = prioridades.map((prioridad: any) => ({
      ...prioridad,
      translatedText: intl.i18n.t(`ticket.prioridad.${prioridad.Denominacion}`),
    }));
  }
});

onMounted(async () => {
  await api.callStandardApi("getTicketById", { showLoading: true }, props.id).then((value: DetailTicketModel) => {
    Object.assign(model, value);
  });

  isInitialLoad.value = false;
  scrollToLastMessage();
});

// Intervalo para refrescar información del ticket
const interval = setInterval(function () {
  if (document.hidden) {
    return;
  }
  refrescarInfoTicket();
  leerMensajes();
}, 20000); // 20 segundos

onUnmounted(() => {
  clearInterval(interval);
});

watch(
  () => model.Mensajes,
  () => {
    scrollToLastMessage();
  },
  { deep: true },
);

const refrescarInfoTicket = async () => {
  await api.callStandardApi("getTicketById", { showLoading: false }, props.id).then((data) => {
    Object.assign(model, {
      ...model,
      Mensajes: data.Mensajes,
      Estado: data.Estado,
      PuedeCerrarse: data.PuedeCerrarse,
      PuedeSolicitarseCierre: data.PuedeSolicitarseCierre,
      PuedeEscribirMensajes: data.PuedeEscribirMensajes,
      Responsable: data.Responsable,
      UserNameResponsable: data.UserNameResponsable,
    });
  });
};

const leerMensajes = async () => {
  await api.callStandardApi("readMensajesTicket", { showLoading: false }, props.id);
};

const esBackoffice = computed(() => {
  return autho.hasPermission(PermissionsApp.GetAllTickets) || autho.hasPermission(PermissionsApp.GetAssociatedTickets);
});

const scrollToLastMessage = () => {
  nextTick(() => {
    const lastMessageElement = document.getElementById("last-message");
    if (lastMessageElement) {
      lastMessageElement.scrollIntoView({ behavior: "instant" });
    }
  });
};

const handlePrioridadChange = async (e: any) => {
  // Evitamos ejecutar esto en la primera carga
  if (isInitialLoad.value) {
    isInitialLoad.value = false;
    return;
  }

  if (model.Prioridad) await api.callStandardApi("setPrioridadTicket", { showLoading: false }, model.Id, model.Prioridad);
};

const handleCloseRequest = async () => {
  let respuesta = await modal.messageBox(
    intl.i18n.t("ticket.detalle.pregunta_solicitud_cierre"),
    intl.i18n.t("ticket.detalle.confirmacion"),
    "okCancel",
    "warning",
    true,
  );
  if (respuesta == messageBoxResult.ok) {
    await api.callStandardApi("askCloseTicket", { showLoading: false }, model.Id).then(async () => {
      await api.callStandardApi("getTicketById", { showLoading: false }, props.id).then((value: DetailTicketModel) => {
        Object.assign(model, value);
      });
    });
  }
};

const handleCloseTicket = async () => {
  let respuesta = await modal.messageBox(
    intl.i18n.t("ticket.detalle.pregunta_aceptar_cierre"),
    intl.i18n.t("ticket.detalle.confirmacion"),
    "okCancel",
    "warning",
    true,
  );
  if (respuesta == messageBoxResult.ok) {
    await api.callStandardApi("acceptCloseTicket", { showLoading: false }, model.Id).then(async () => {
      await api.callStandardApi("getTicketById", { showLoading: false }, props.id).then((value: DetailTicketModel) => {
        Object.assign(model, value);
      });
    });
  }
};

const handleRejectCloseTicket = async () => {
  let respuesta = await modal.messageBox(
    intl.i18n.t("ticket.detalle.pregunta_rechazar_cierre"),
    intl.i18n.t("ticket.detalle.confirmacion"),
    "okCancel",
    "warning",
    true,
  );
  if (respuesta == messageBoxResult.ok) {
    await api.callStandardApi("rejectCloseTicket", { showLoading: false }, model.Id).then(async () => {
      await api.callStandardApi("getTicketById", { showLoading: false }, props.id).then((value: DetailTicketModel) => {
        Object.assign(model, value);
      });
    });
  }
};

const sendMessage = async () => {
  var mensaje = createEmptyMensajeTicketModel();
  mensaje.Mensaje = textoMensaje.value;

  if (mensaje.Mensaje != "") {
    try {
      await setAdjuntos(mensaje, ficheros.value!);
      await api.callStandardApi("addMessageTicket", { showLoading: true }, model.Id, mensaje).then(async () => {
        await api.callStandardApi("getTicketById", { showLoading: false }, props.id).then((value: DetailTicketModel) => {
          Object.assign(model, value);
          limpiarAreaRespuesta();
        });
      });
      return true;
    } catch (e) {
      toastError(intl.i18n.t("ticket.detalle.mensaje_fallo"));
      return false;
    }
  } else {
    toastError(intl.i18n.t("ticket.detalle.mensaje_vacio"));
    return false;
  }
};

const createEmptyMensajeTicketModel = (): CreateMensajeTicketModel => ({
  Mensaje: "",
  Adjuntos: [],
  Formato: "text/plain",
});

const limpiarAreaRespuesta = () => {
  textoMensaje.value = "";
  ficheros.value = {};
};

async function setAdjuntos(modalModel: CreateMensajeTicketModel, ficheros: Ficheros): Promise<void> {
  for (const key of Object.getOwnPropertyNames(ficheros)) {
    const fichero = ficheros[key];
    if (fichero.status === "new" || fichero.status === "modified") {
      const uploadId = await api.callStandardApi("postDocumentoTemp", { showLoading: true }, fichero.fileName!, fichero.file!);
      modalModel.Adjuntos.push({ Nombre: fichero.fileName!, Url: uploadId });
    } else if (fichero.status === "unchanged") {
      //modalModel.Mensaje.Adjuntos.push({ Nombre: fichero.fileName!, Url: null });  // Por si alguna vez tenemos multiple subida con edicion : de nada por el hint
    }
  }
}

async function urlToBlob(ruta: string) {
  const response = await fetch(ruta);
  const data = await response.blob();
  const blob = new Blob([data], { type: "application/pdf;charset=utf-8" });
  return blob;
}

async function descargaAdjunto(ruta: string) {
  var tempUrl = await api.callStandardApi("documentosGenerateTempUrl", { showLoading: false }, ruta);

  urlToBlob(tempUrl).then((blob) => {
    try {
      var file = ruta.split("/").pop();
      FileSaver.saveAs(blob, file);
    } catch (error) {
      console.error("error", error);
    }
  });
}

const todosLosAdjuntos = computed(() => {
  const adjuntos: string[] = [];
  model.Mensajes?.forEach((mensaje) => {
    if (mensaje.Adjuntos && mensaje.Adjuntos.length > 0) {
      adjuntos.push(...mensaje.Adjuntos);
    }
  });
  return adjuntos;
});

const showSolicitarCierre = computed((): boolean => {
  return model.PuedeSolicitarseCierre && model.UserNameResponsable === auth.userInfo?.username;
});

const showAutoAsignarResponsable = computed((): boolean => {
  return props.esBackoffice && model.UserNameResponsable !== auth.userInfo?.username;
});

const showAsignarResponsable = computed((): boolean => {
  return props.esBackoffice && model.UserNameResponsable === auth.userInfo?.username;
});

const autoAsignarResponsable = async (): Promise<void> => {
  let respuesta = await modal.messageBox(
    intl.i18n.t("ticket.detalle.responsable_mensaje_confirmacion"),
    intl.i18n.t("ticket.detalle.responsable_mensaje_confirmacion_titulo"),
    "okCancel",
    "warning",
  );
  if (respuesta !== messageBoxResult.ok) return;

  try {
    await api.callStandardApi("selfAssignResponsableTicket", { showLoading: true }, props.id);
    await refrescarInfoTicket();
  } catch (error) {
    toastError(intl.i18n.t("ticket.detalle.responsable_mensaje_error_cambio"));
  }
};

const asignarResponsable = async (): Promise<void> => {
  let dataSource = await api.callStandardApi("getUsuariosBackofficeSelectorByIdTicket", { showLoading: false }, props.id);

  let idResponsable = (await modal.showModal(
    InputSelectBox,
    {
      label: intl.i18n.t("ticket.detalle.responsable_selector_label"),
      required: true,
      dataSource: dataSource,
    },
    {
      title: intl.i18n.t("ticket.detalle.responsable_selector"),
      width: 450,
      height: 270,
    },
  )) as InputSelectBoxModalResult;

  if (!idResponsable?.value) return;

  if (idResponsable?.result === messageBoxResult.ok) {
    await api
      .callStandardApi("assignResponsableTicket", { showLoading: true }, props.id, idResponsable.value)
      .then(() => {
        toastSuccess(intl.i18n.t("ticket.detalle.responsable_mensaje_cambio_exito"));
        refrescarInfoTicket();
      })
      .catch(() => toastError(intl.i18n.t("ticket.detalle.responsable_mensaje_cambio_error")));
  }
};
</script>

<style scoped lang="scss">


.ticket-layout {
  display: flex;
  height: calc(100vh - 200px);
}

h6 {
  font-size: 1.25em !important;
}

.ticket-main {
  flex: 1;
  display: flex;
  max-height: 100%;
  flex-direction: column;
  overflow: hidden;
}

.ticket-header {
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid var(--border-default-default);
  padding-bottom: var(--spacing-xs);
  gap: var(--spacing-lg);

  .ticket-header-left {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-2xs);
    flex-grow: 1;
  }

  .ticket-header-right {
    display: flex;
    gap: var(--spacing-sm);
    align-items: flex-end;
  }
}



.ticket-asunto {
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
}



.ticket-messages {
  width: 95%;
  margin: var(--spacing-lg) auto;
  overflow: auto;
  flex: 1;

  &::-webkit-scrollbar {
    width: 8px;
    background-color: var(--grid-alt-row-background);
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--background-primary-default);
    border-radius: var(--border-radius-lg);
  }
}

.message {
  display: flex;
  margin-bottom: var(--spacing-lg);
  max-width: 100%;
}

.message-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: var(--spacing-xs);
}

.message-status {
  display: flex;
  align-items: center;
  margin-left: var(--spacing-xs);
}

.check-single {
  color: var(--icon-primary-default);
  font-size: 12px;
}

.check-double {
  color: var(--text-tertiary-default);
  font-size: 12px;
}

.user-message {
  justify-content: flex-end;
}

.reply-message {
  justify-content: flex-start;
}

.message-content {
  background: var(--background-normal-light);
  padding: var(--spacing-xs);
  border-radius: var(--border-radius-lg);
  max-width: 50%;
  min-width: 30%;
  white-space: pre-wrap;
}

.message-avatar img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
}

.ticket-reply {
  width: 95%;
  margin: 20px auto 0;
  display: flex;
  //align-items: center;
  background: var(--background-normal-light);
  padding: var(--spacing-sm) var(--spacing-sm) var(--spacing-lg);
  border-radius: var(--border-radius-lg);
  max-height: 500px;

  &.con-adjuntos {
    flex-direction: column;
  }
}

.reply-input {
  display: flex;
  flex-grow: 1;
  /* Permite que crezca ocupando el espacio disponible */
  flex-direction: row;
  margin-top: var(--spacing-xs);
  align-items: center;
}

.reply-input textarea {
  flex-grow: 1;
  /* Hace que crezca cuando el contenedor se expanda */
  resize: none;
  width: 100%;
  padding: var(--spacing-xs);
  border: none;
  border-radius: var(--border-radius-xl);
  min-height: 80px;
  /* Evita que sea demasiado pequeño */
  max-height: 300px;
  /* Opcional, para evitar crecimiento excesivo */
}

.send-button {
  background: var(--background-primary-default);
  color: white;
  border: none;
  padding: var(--spacing-xs);
  margin-left: var(--spacing-lg);
  margin-right: var(--spacing-lg);
  width: 50px;
  min-width: 50px;
  max-height: 50px;
  height: 50px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 24px;
}

hr {
  margin: 0 !important;
}

.circle-icon {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  min-width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: var(--background-secondary-default);
  color: var(--text-secondary-on-action);
  font-size: 25px;
  margin: var(--spacing-2xs);
}

.circle-icon-system {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  min-width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: var(--background-primary-default);
  color: var(--text-primary-on-action);
  font-size: 16px;
  margin: var(--spacing-2xs);
}

.circle-icon i {
  font-size: 16px;
}

.ticket-close-request .close-request-button {
  min-width: 215px;
}

div#prioridad-select {
  min-width: 165px;
}

.ticket-close-actions .close-ticket-button,
.ticket-close-actions .reject-close-button {
  padding: var(--spacing-xs) var(--spacing-md);
  margin-right: var(--spacing-xs);
}

.reply-attach-file {
  margin-right: var(--spacing-sm);
  flex-shrink: 0;
  /* Evita que este elemento se reduzca */
  padding: 0 var(--spacing-sm);
  display: flex;
  flex-wrap: wrap;
  /* Permite múltiples líneas de adjuntos */
  //gap: 10px;
  gap: var(--spacing-2xs);
}

.documento {
  cursor: pointer;
  margin-top: var(--spacing-xs);
}

.ticket-close-actions {
  display: flex;
  justify-content: space-between;
  background-color: var(--grid-alt-row-background);
  padding: var(--spacing-sm);
  align-items: center;
}

.ticket-sidebar {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.ticket-sidebar :deep(.sidebar-box) {
  border: 1px solid var(--border-default-default);
  border-radius: var(--border-radius-lg);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: var(--spacing-lg) var(--spacing-md);
}

.ticket-sidebar :deep(.sidebar-box h6) {
  font-weight: 600;
  font-size: 1em;
}

@media (max-width: 768px) {
  .ticket-layout {
    height: auto;
  }

  .ticket-main {
    max-width: 100%;
    width: 100%;
  }

  .ticket-sidebar {
    margin-bottom: 20px;
  }

  .ticket-container {
    padding: 0 0 var(--spacing-2xs);
  }

  .ticket-header {
    align-items: start;
    flex-wrap: wrap;
  }

  .ticket-messages {
    width: 100%;
    flex: auto;
  }

  .message-content {
    min-width: calc(100% - 50px);
  }

  .ticket-reply {
    flex-direction: column;
    width: 100%;
  }

  .send-button {
    margin: 0 var(--spacing-2xs) 0 var(--spacing-sm);
  }

  .ticket-header .ticket-header-right {
    flex-wrap: wrap;
  }
}
</style>
